import { Link } from 'react-router-dom'
import {
  Search,
  CalendarCheck,
  HandshakeIcon,
  Star,
  Shield,
  ArrowRight,
  MessageCircle,
  Clock,
  Repeat,
  CheckCircle,
  MapPin,
  CreditCard,
} from 'lucide-react'
import MarketingHeader from '../components/MarketingHeader'
import MarketingFooter from '../components/MarketingFooter'

const steps = [
  {
    num: '01',
    icon: Search,
    title: 'Find what you need',
    body: 'Browse hundreds of local listings by category, location, or date. Use filters to narrow by price, distance, and availability. Every listing shows real photos, honest reviews, and upfront pricing — no hidden fees.',
    detail: [
      'Search by keyword, category, or ZIP code',
      'Filter by price, distance, and availability',
      'Read verified reviews from past renters',
      'Compare multiple listings side by side',
    ],
    image: '/images/marketing/howitworks-drone.jpg',
    imageAlt: 'A hand reaching for a hovering drone at sunset',
  },
  {
    num: '02',
    icon: CalendarCheck,
    title: 'Book with confidence',
    body: 'Select your dates, send a booking request or use Instant Book on eligible listings. Your payment is held securely until the rental begins — the owner only receives funds after handoff.',
    detail: [
      'Instant Book or request-to-approve listings',
      'Secure payment held in escrow',
      'Free cancellation up to 48 hours before',
      'Rental agreement generated automatically',
    ],
    image: '/images/marketing/howitworks-soundsystem.jpg',
    imageAlt: 'A hand holding a portable speaker with mountains in the background',
  },
  {
    num: '03',
    icon: HandshakeIcon,
    title: 'Pick up & enjoy',
    body: 'Coordinate pickup directly with your host via our in-app messaging. Take a quick walkthrough, snap condition photos together, and head out. Return when done and leave a review.',
    detail: [
      'In-app messaging with your host',
      'Condition photo checklist at pickup',
      '24/7 support if anything goes wrong',
      'Leave a review after return',
    ],
    image: '/images/marketing/kayaking-lake.jpg',
    imageAlt: 'Person kayaking on a lake',
  },
]

const ownerSteps = [
  {
    icon: Star,
    title: 'List in 5 minutes',
    body: 'Add photos, set your price and availability, and go live. We guide you through each field.',
  },
  {
    icon: Shield,
    title: 'We handle the risk',
    body: 'Every rental includes basic damage protection. You set your own deposit amount as a backup.',
  },
  {
    icon: CreditCard,
    title: 'Get paid fast',
    body: 'Funds are released to your account within 24 hours of a completed rental. No delays.',
  },
  {
    icon: MessageCircle,
    title: 'Stay in control',
    body: 'Approve or decline requests. Block dates. Adjust pricing. Your listing, your rules.',
  },
]

const faqs = [
  {
    q: 'What if the item is damaged during my rental?',
    a: 'Every rental on I Got That U.S. includes basic protection covering accidental damage. Owners can also set a refundable security deposit. For major incidents our support team mediates a fair resolution.',
  },
  {
    q: 'Can I cancel my booking?',
    a: "Yes. Rentals cancelled more than 48 hours before the start time receive a full refund. Cancellations within 48 hours may be subject to the owner's cancellation policy shown on the listing.",
  },
  {
    q: 'How do I know the gear is in good condition?',
    a: 'Owners are required to accurately describe condition and upload current photos. A shared condition checklist is completed at pickup and return, and all reviews are publicly visible.',
  },
  {
    q: 'Is there a fee to list my items?',
    a: 'Listing is completely free. We charge a small service fee (typically 12–15%) only when a rental is completed — so you pay nothing until you earn.',
  },
  {
    q: 'What areas do you serve?',
    a: 'I Got That U.S. is launching in Anchorage and surrounding communities in Summer 2026, with plans to expand statewide. Join the waitlist to be notified when we reach your area.',
  },
]

export default function HowItWorks() {
  return (
    <div className="min-h-screen bg-background text-foreground" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <MarketingHeader alwaysSolid />

      {/* ── Page Hero ── */}
      <section className="pt-32 pb-20 px-6 bg-secondary">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-4">Simple. Transparent. Local.</p>
          <h1
            className="text-5xl md:text-6xl font-semibold leading-tight mb-6"
            style={{ fontFamily: "'Fraunces', serif" }}
          >
            Renting from neighbors
            <br />
            <span className="italic font-light text-primary">should be this easy</span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            I Got That U.S. connects people who need gear with neighbors who have it sitting idle. No
            corporate warehouses. No one-size-fits-all pricing. Just your community, sharing what it has.
          </p>
        </div>
      </section>

      {/* ── 3 Steps — Renter ── */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-3">For Renters</p>
            <h2 className="text-4xl md:text-5xl font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
              Three steps to your next adventure
            </h2>
          </div>

          <div className="flex flex-col gap-24">
            {steps.map((step, i) => {
              const Icon = step.icon
              const isEven = i % 2 === 1
              return (
                <div
                  key={step.num}
                  className={`grid md:grid-cols-2 gap-12 items-center ${isEven ? 'md:[&>*:first-child]:order-2' : ''}`}
                >
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <div
                        className="text-6xl font-semibold text-primary/10 leading-none"
                        style={{ fontFamily: "'Fraunces', serif" }}
                      >
                        {step.num}
                      </div>
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Icon size={18} className="text-primary" />
                      </div>
                    </div>
                    <h3
                      className="text-3xl font-semibold mb-4"
                      style={{ fontFamily: "'Fraunces', serif" }}
                    >
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed mb-6">{step.body}</p>
                    <ul className="flex flex-col gap-3">
                      {step.detail.map((d) => (
                        <li key={d} className="flex items-start gap-3">
                          <CheckCircle size={16} className="text-primary mt-0.5 shrink-0" />
                          <span className="text-sm text-foreground/80">{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="relative rounded-2xl overflow-hidden bg-muted" style={{ aspectRatio: '4/3' }}>
                    <img
                      src={step.image}
                      alt={step.imageAlt}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent" />
                    <div className="absolute bottom-4 left-4 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur-sm text-xs font-semibold text-foreground flex items-center gap-1.5">
                      <Icon size={12} className="text-primary" />
                      Step {step.num}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6">
        <div className="border-t border-border" />
      </div>

      {/* ── For Owners ── */}
      <section className="py-24 px-6 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute -top-20 right-0 w-[500px] h-[500px] rounded-full bg-accent blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <p className="text-xs font-semibold tracking-widest text-accent uppercase mb-4">For Owners</p>
              <h2
                className="text-4xl md:text-5xl font-semibold !text-white leading-tight mb-6"
                style={{ fontFamily: "'Fraunces', serif" }}
              >
                Your gear earns
                <br />
                <span className="italic font-light">while you sleep</span>
              </h2>
              <p className="text-primary-foreground/70 leading-relaxed mb-8 max-w-md">
                Got a kayak that sits in the garage all winter? A drill you use once a month? List it, name
                your price, and let neighbors put it to work. You stay in full control.
              </p>
              <div className="flex items-center gap-3">
                <div className="px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-center">
                  <div className="text-2xl font-semibold text-accent" style={{ fontFamily: "'Fraunces', serif" }}>$0</div>
                  <div className="text-xs text-primary-foreground/60 mt-0.5">to list</div>
                </div>
                <div className="px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-center">
                  <div className="text-2xl font-semibold text-accent" style={{ fontFamily: "'Fraunces', serif" }}>85%</div>
                  <div className="text-xs text-primary-foreground/60 mt-0.5">you keep</div>
                </div>
                <div className="px-4 py-3 rounded-xl bg-white/10 border border-white/15 text-center">
                  <div className="text-2xl font-semibold text-accent" style={{ fontFamily: "'Fraunces', serif" }}>24h</div>
                  <div className="text-xs text-primary-foreground/60 mt-0.5">payout</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {ownerSteps.map((s) => {
                const Icon = s.icon
                return (
                  <div key={s.title} className="bg-white/8 rounded-xl p-5 border border-white/10">
                    <div className="w-9 h-9 rounded-lg bg-accent/20 flex items-center justify-center mb-3">
                      <Icon size={16} className="text-accent" />
                    </div>
                    <h4 className="font-semibold text-white mb-1.5 text-sm" style={{ fontFamily: "'Fraunces', serif" }}>{s.title}</h4>
                    <p className="text-xs text-primary-foreground/60 leading-relaxed">{s.body}</p>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="mt-12">
            <Link
              to="/list-item"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-accent text-accent-foreground font-semibold text-sm hover:bg-amber-400 transition-colors"
            >
              Start listing for free <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Timeline visual ── */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-3">Timeline</p>
            <h2 className="text-4xl font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
              What a typical rental looks like
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-6 top-0 bottom-0 w-px bg-border md:left-1/2" />
            <div className="flex flex-col gap-10">
              {[
                { icon: Search, time: 'Day before', label: 'You find the listing and book instantly online.' },
                { icon: MessageCircle, time: 'Evening', label: 'Host confirms and you message to arrange pickup time.' },
                { icon: MapPin, time: 'Morning of rental', label: 'You meet, walk through the gear, and snap condition photos.' },
                { icon: Clock, time: 'Your day', label: 'Go on your adventure. Take the kayak out. Explore.' },
                { icon: Repeat, time: 'Return', label: "Bring it back in agreed condition. Host releases your deposit." },
                { icon: Star, time: 'Afterward', label: 'Leave a review. The community grows a little stronger.' },
              ].map((item, i) => {
                const Icon = item.icon
                const isRight = i % 2 === 1
                return (
                  <div key={item.label} className={`relative flex items-start gap-6 md:gap-0 ${isRight ? 'md:flex-row-reverse' : ''}`}>
                    <div className={`hidden md:block w-1/2 ${isRight ? 'pl-10' : 'pr-10 text-right'}`}>
                      <span className="text-xs font-semibold text-primary uppercase tracking-widest">{item.time}</span>
                      <p className="text-sm text-muted-foreground mt-1">{item.label}</p>
                    </div>
                    <div className="relative z-10 flex-shrink-0 w-12 h-12 rounded-full bg-primary flex items-center justify-center shadow-md">
                      <Icon size={16} className="text-white" />
                    </div>
                    <div className="md:hidden flex-1">
                      <span className="text-xs font-semibold text-primary uppercase tracking-widest">{item.time}</span>
                      <p className="text-sm text-muted-foreground mt-1">{item.label}</p>
                    </div>
                    <div className="hidden md:block w-1/2" />
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 px-6 bg-secondary">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-xs font-semibold tracking-widest text-primary uppercase mb-3">Common Questions</p>
            <h2 className="text-4xl font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>FAQs</h2>
          </div>
          <div className="flex flex-col gap-4">
            {faqs.map((faq) => (
              <div key={faq.q} className="bg-card rounded-xl p-6 border border-border">
                <h4 className="font-semibold text-base mb-2" style={{ fontFamily: "'Fraunces', serif" }}>{faq.q}</h4>
                <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-16 px-6 bg-accent">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-4xl font-semibold text-accent-foreground mb-4" style={{ fontFamily: "'Fraunces', serif" }}>
            Ready to get started?
          </h2>
          <p className="text-accent-foreground/70 mb-8">
            Browse available gear near you, or list your first item in under 5 minutes.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/browse" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold text-sm hover:bg-primary/90 transition-colors">
              Browse rentals <ArrowRight size={15} />
            </Link>
            <Link to="/list-item" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white text-foreground font-semibold text-sm hover:bg-white/90 transition-colors border border-border">
              List your item
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
